import { PERMISSION_KEYS, type PermissionKey } from './registry';
import type { Scope } from './scope';

/**
 * System roles exist in every agency and cannot be deleted. `owner` additionally
 * cannot be edited: it holds every permission by definition (see
 * `resolvePermissions`), including ones added to the registry later.
 */
export type SystemRoleKey = 'owner' | 'admin' | 'staff';

export interface DefaultRole {
  key: SystemRoleKey | null;
  name: string;
  description: string;
  color: string;
  grants: Partial<Record<PermissionKey, Scope>>;
}

const all = (scope: Scope) =>
  Object.fromEntries(PERMISSION_KEYS.map((k) => [k, scope])) as Record<PermissionKey, Scope>;

const operational = (scope: Scope): DefaultRole['grants'] => ({
  'members.view': 'tenant',
  'sales.view': scope,
  'sales.create': 'tenant',
  'sales.edit': scope,
  'services.create': 'tenant',
  'services.edit': scope,
  'services.checkin': 'tenant',
  'payments.view': scope,
  'payments.create': 'tenant',
  'proposals.view': scope,
  'proposals.create': 'tenant',
  'proposals.edit': scope,
  'options.view': scope,
  'options.create': 'tenant',
  'options.edit': scope,
  'options.confirm': 'tenant',
  'tasks.view': scope,
  'tasks.create': 'tenant',
  // A simple to-do list: anyone may hand a task to a colleague.
  'tasks.assign': 'tenant',
  'customers.view': 'tenant',
  'customers.manage': 'tenant',
  'customers.identity': 'tenant',
  'customers.documents': 'tenant',
  'reports.view': scope,
});

/** The roles every new agency starts with. */
export const DEFAULT_ROLES: readonly DefaultRole[] = [
  {
    key: 'owner',
    name: 'Sahip',
    description: 'Acentenin sahibi. Her şeyi görür ve yönetir, kısıtlanamaz.',
    color: '#6B1D34',
    grants: {},
  },
  {
    key: 'admin',
    name: 'Yönetici',
    description: 'Ekibi, rolleri ve ayarları yönetir; tüm kayıtlara erişir.',
    color: '#2F4A7A',
    grants: all('tenant'),
  },
  {
    key: null,
    name: 'Birim Sorumlusu',
    description: 'Biriminin satışlarını, opsiyonlarını ve görevlerini yönetir.',
    color: '#1F6F5C',
    grants: {
      ...operational('department'),
      'tasks.assign': 'tenant',
      'sales.delete': 'department',
      'customers.archive': 'tenant',
    },
  },
  {
    key: 'staff',
    name: 'Satış Temsilcisi',
    description: 'Kendi satışlarını, tekliflerini ve opsiyonlarını yönetir.',
    color: '#8A5A12',
    grants: operational('own'),
  },
  {
    key: null,
    name: 'Vize Uzmanı',
    description: 'Vize başvurularını ve belge şablonlarını yönetir.',
    color: '#5B3F8C',
    grants: {
      'members.view': 'tenant',
      'visa.view': 'tenant',
      'visa.create': 'tenant',
      'visa.edit': 'tenant',
      'visa.documents': 'tenant',
      'tasks.view': 'own',
      'customers.view': 'tenant',
      'customers.manage': 'tenant',
      'customers.identity': 'tenant',
      'customers.documents': 'tenant',
    },
  },
  {
    key: null,
    name: 'Muhasebe',
    description: 'Ödemeleri, faturaları ve raporları yönetir.',
    color: '#3D5A66',
    grants: {
      'members.view': 'tenant',
      'customers.view': 'tenant',
      'sales.view': 'tenant',
      'sales.invoice': 'tenant',
      'sales.bizimhesap': 'tenant',
      'payments.view': 'tenant',
      'payments.create': 'tenant',
      'payments.edit': 'tenant',
      'cards.manage': 'tenant',
      'suppliers.view': 'tenant',
      'suppliers.manage': 'tenant',
      'reports.view': 'tenant',
    },
  },
];

export const DEFAULT_DEPARTMENTS = [
  { name: 'Satış', color: '#2F4A7A' },
  { name: 'Operasyon', color: '#1F6F5C' },
  { name: 'Vize', color: '#5B3F8C' },
  { name: 'Muhasebe', color: '#3D5A66' },
] as const;
